import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Chip,
    IconButton,
    useTheme,
    Divider,
    Paper,
    Tooltip,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemIcon
} from '@mui/material';
import {
    Close,
    AccessTime,
    Flag,
    Edit,
    Delete,
    CheckCircle,
    RadioButtonUnchecked,
    Assignment,
    Notes
} from '@mui/icons-material';
import { Task } from '../../types/task';

interface TaskDetailProps {
    task: Task | null;
    open: boolean;
    onClose: () => void;
    onEdit: (task: Task) => void;
    onDelete: (id: number) => void;
    onStatusChange: (id: number, completed: boolean) => void;
}

interface Subtask {
    text: string;
    completed: boolean;
}

const TaskDetail: React.FC<TaskDetailProps> = ({
    task,
    open,
    onClose,
    onEdit,
    onDelete,
    onStatusChange
}) => {
    const theme = useTheme();
    const [parsedSubtasks, setParsedSubtasks] = useState<Subtask[]>([]);
    const [mainText, setMainText] = useState<string>('');

    useEffect(() => {
        // Reset state when task changes
        if (!task) {
            setMainText('');
            setParsedSubtasks([]);
            return;
        }

        // If task has description, parse it
        if (task.description) {
            const { mainText, subtasks } = parseSubtasks(task.description);
            setMainText(mainText);
            setParsedSubtasks(subtasks);
        } else {
            // Clear previous data if no description
            setMainText('');
            setParsedSubtasks([]);
        }
    }, [task]);

    if (!task) return null;

    const getPriorityColor = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'high':
                return theme.palette.error.main;
            case 'medium':
                return theme.palette.warning.main;
            case 'low':
                return theme.palette.success.main;
            default:
                return theme.palette.info.main;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'completed':
                return theme.palette.success.main;
            case 'in-progress':
                return theme.palette.warning.main;
            case 'todo':
                return theme.palette.info.main;
            default:
                return theme.palette.info.main;
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'No due date';
        try {
            const date = new Date(dateString);
            const options: Intl.DateTimeFormatOptions = {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            };
            return date.toLocaleDateString('en-US', options);
        } catch (e) {
            return 'Invalid date';
        }
    };

    const parseSubtasks = (description: string): { mainText: string, subtasks: Subtask[] } => {
        const subtaskSectionRegex = /## Subtasks:([\s\S]*?)(?:$|(?=## ))/;
        const subtaskMatch = description.match(subtaskSectionRegex);

        let mainText = description;
        let subtaskList: Subtask[] = [];

        if (subtaskMatch) {
            // Remove the subtask section from the main text
            mainText = description.replace(subtaskSectionRegex, '').trim();

            // Parse the subtasks
            const subtaskSection = subtaskMatch[1].trim();
            const subtaskLines = subtaskSection.split('\n').filter(line => line.trim().startsWith('- ['));

            subtaskList = subtaskLines.map(line => {
                const isCompleted = line.includes('- [x]') || line.includes('- [X]');
                const text = line.replace(/- \[[x\s]\]\s*/, '').trim();
                return { text, completed: isCompleted };
            });
        }

        return { mainText, subtasks: subtaskList };
    };

    // Check if text contains notes about books
    const hasBookNotes = mainText.toLowerCase().includes('book') ||
        mainText.toLowerCase().includes('read') ||
        mainText.toLowerCase().includes('goodreads');

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    background: 'rgba(30, 30, 30, 0.9)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                    overflow: 'hidden'
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                    pb: 2,
                    background: `linear-gradient(to right, ${getPriorityColor(task.priority)}20, transparent)`,
                    borderLeft: `4px solid ${getPriorityColor(task.priority)}`
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={() => onStatusChange(task.id, !task.completed)}
                        sx={{
                            color: task.completed ? theme.palette.success.main : theme.palette.text.secondary,
                            '&:hover': {
                                backgroundColor: task.completed
                                    ? `${theme.palette.success.main}20`
                                    : `${theme.palette.primary.main}20`,
                            }
                        }}
                    >
                        {task.completed ? <CheckCircle /> : <RadioButtonUnchecked />}
                    </IconButton>
                    <Typography
                        variant="h5"
                        component="span"
                        sx={{
                            fontWeight: 'bold',
                            textDecoration: task.completed ? 'line-through' : 'none',
                            color: task.completed ? 'text.secondary' : 'text.primary',
                        }}
                    >
                        {task.title}
                    </Typography>
                </Box>
                <Box>
                    <Tooltip title="Close">
                        <IconButton onClick={onClose} edge="end" aria-label="close">
                            <Close />
                        </IconButton>
                    </Tooltip>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip
                            icon={<AccessTime fontSize="small" />}
                            label={`Due: ${formatDate(task.due_date)}`}
                            size="medium"
                            sx={{
                                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                backdropFilter: 'blur(5px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                            }}
                        />

                        <Chip
                            icon={<Flag fontSize="small" />}
                            label={`Priority: ${task.priority}`}
                            size="medium"
                            sx={{
                                backgroundColor: `${getPriorityColor(task.priority)}20`,
                                color: getPriorityColor(task.priority),
                                border: `1px solid ${getPriorityColor(task.priority)}50`,
                            }}
                        />

                        <Chip
                            label={`Status: ${task.status}`}
                            size="medium"
                            sx={{
                                backgroundColor: `${getStatusColor(task.status)}20`,
                                color: getStatusColor(task.status),
                                border: `1px solid ${getStatusColor(task.status)}50`,
                            }}
                        />
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)' }} />

                    {/* Main content area */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Description section if there is main text */}
                        {mainText && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                    border: '1px solid rgba(255, 255, 255, 0.05)',
                                    borderRadius: '8px',
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                    pb: 1,
                                    mb: 2
                                }}>
                                    <Notes sx={{ mr: 1, color: theme.palette.info.main }} />
                                    <Typography variant="h6" color="info.main">
                                        Description
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body1"
                                    component="div"
                                    sx={{
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {mainText}
                                </Typography>
                            </Paper>
                        )}

                        {/* Subtasks section */}
                        {parsedSubtasks.length > 0 && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                    border: '1px solid rgba(255, 255, 255, 0.05)',
                                    borderRadius: '8px',
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                    pb: 1,
                                    mb: 2
                                }}>
                                    <Assignment sx={{ mr: 1, color: theme.palette.primary.main }} />
                                    <Typography variant="h6" color="primary">
                                        Subtasks
                                    </Typography>
                                </Box>

                                <List disablePadding>
                                    {parsedSubtasks.map((subtask, index) => (
                                        <ListItem
                                            key={index}
                                            disablePadding
                                            sx={{
                                                py: 1,
                                                px: 1,
                                                borderRadius: '4px',
                                                mb: 1,
                                                bgcolor: 'rgba(255, 255, 255, 0.03)',
                                                '&:hover': {
                                                    bgcolor: 'rgba(255, 255, 255, 0.05)'
                                                }
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: '40px' }}>
                                                <Checkbox
                                                    checked={subtask.completed}
                                                    icon={<RadioButtonUnchecked />}
                                                    checkedIcon={<CheckCircle />}
                                                    sx={{
                                                        color: 'text.secondary',
                                                        '&.Mui-checked': {
                                                            color: theme.palette.success.main
                                                        }
                                                    }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            textDecoration: subtask.completed ? 'line-through' : 'none',
                                                            color: subtask.completed ? 'text.secondary' : 'text.primary',
                                                            fontWeight: 500
                                                        }}
                                                    >
                                                        {subtask.text}
                                                    </Typography>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Paper>
                        )}

                        {/* Book notes section if applicable */}
                        {hasBookNotes && (
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 3,
                                    backgroundColor: 'rgba(0, 0, 0, 0.2)',
                                    border: '1px solid rgba(255, 255, 255, 0.05)',
                                    borderRadius: '8px',
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                                    pb: 1,
                                    mb: 2
                                }}>
                                    <Notes sx={{ mr: 1, color: theme.palette.secondary.main }} />
                                    <Typography variant="h6" color="secondary">
                                        Reading Notes
                                    </Typography>
                                </Box>
                                <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.2)', borderRadius: '8px' }}>
                                    <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                                        Recommended Resources:
                                    </Typography>
                                    <Box component="ul" sx={{ pl: 2, m: 0 }}>
                                        <Box component="li" sx={{ mb: 1 }}>
                                            <Typography variant="body2">
                                                <strong>Goodreads</strong> - Find books based on your interests and read reviews
                                            </Typography>
                                        </Box>
                                        <Box component="li" sx={{ mb: 1 }}>
                                            <Typography variant="body2">
                                                <strong>Amazon Books</strong> - Check bestseller lists and recommendations
                                            </Typography>
                                        </Box>
                                        <Box component="li" sx={{ mb: 1 }}>
                                            <Typography variant="body2">
                                                <strong>Local Library</strong> - Browse available titles and get personalized recommendations
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>
                        )}

                        {!task.description && (
                            <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}>
                                No description provided
                            </Typography>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            Created: {new Date(task.created_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            ID: {task.id}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255, 255, 255, 0.1)', justifyContent: 'space-between' }}>
                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => {
                        onDelete(task.id);
                        onClose();
                    }}
                    sx={{
                        borderColor: theme.palette.error.main,
                        color: theme.palette.error.main,
                        '&:hover': {
                            backgroundColor: `${theme.palette.error.main}20`,
                            borderColor: theme.palette.error.light,
                        }
                    }}
                >
                    Delete
                </Button>

                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        sx={{
                            borderColor: 'rgba(255, 255, 255, 0.3)',
                            '&:hover': {
                                borderColor: 'rgba(255, 255, 255, 0.5)',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            }
                        }}
                    >
                        Close
                    </Button>

                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<Edit />}
                        onClick={() => {
                            onEdit(task);
                            onClose();
                        }}
                        sx={{
                            background: 'linear-gradient(45deg, #00e5ff 30%, #2979ff 90%)',
                            boxShadow: '0 0 10px rgba(0, 229, 255, 0.5)',
                            '&:hover': {
                                boxShadow: '0 0 20px rgba(0, 229, 255, 0.8)',
                            }
                        }}
                    >
                        Edit
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default TaskDetail;